import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { publicRoutes } from './routes/public.routes'
import { privateRoutes } from './routes/private.routes'
import { ProtectedRoute } from './guards'
import { RouteWrapper } from './components/RouteWrapper'
import { AppRoutes, LayoutType } from './routes.types'

import { FullScreenLoader } from '@/components/common/FullScreenLoader'
import { AppLayout } from '@/layouts/AppLayout'
import { ProtectedMinimalLayout } from '@/layouts/ProtectedMinimalLayout'
import type { RootState } from '@/store/store'

export function AppRouter() {
    const { user, isLoading } = useSelector((state: RootState) => state.auth)

    // Show loading while checking initial auth state
    if (isLoading) {
        return <FullScreenLoader text="Loading application..." noBackground />
    }

    return (
        <Routes>
            {/* Root redirect */}
            <Route
                path="/"
                element={
                    user ? <Navigate to={AppRoutes.DASHBOARD} replace /> : <Navigate to={AppRoutes.LOGIN} replace />
                }
            />

            {/* Public routes - auth and minimal layout routes without layout wrapper */}
            {publicRoutes.map((route) => (
                <Route
                    key={route.path}
                    path={route.path}
                    element={
                        <ProtectedRoute
                            requireAuth={route.requireAuth}
                            requireOrganization={route.requireOrganization}
                            requiredPermission={route.requiredPermission}
                        >
                            <RouteWrapper
                                component={route.component}
                                title={route.title}
                                description={route.description}
                                fallbackType="auth"
                            />
                        </ProtectedRoute>
                    }
                />
            ))}

            {/* Private routes - wrapped based on layout type */}
            {privateRoutes.map((route) => {
                const routeContent = (
                    <ProtectedRoute
                        requireAuth={route.requireAuth}
                        requireOrganization={route.requireOrganization}
                        requiredPermission={route.requiredPermission}
                    >
                        <RouteWrapper
                            component={route.component}
                            title={route.title}
                            description={route.description}
                            fallbackType={route.layout === LayoutType.APP ? 'app' : 'auth'}
                        />
                    </ProtectedRoute>
                )

                // Wrap route content with appropriate layout
                const getLayoutWrapper = () => {
                    switch (route.layout) {
                        case LayoutType.APP:
                            return <AppLayout>{routeContent}</AppLayout>
                        case LayoutType.PROTECTED_MINIMAL:
                            return <ProtectedMinimalLayout>{routeContent}</ProtectedMinimalLayout>
                        default:
                            return routeContent
                    }
                }

                return <Route key={route.path} path={route.path} element={getLayoutWrapper()} />
            })}

            {/* Catch all route for 404 */}
            <Route path="*" element={<Navigate to={AppRoutes.NOT_FOUND} replace />} />
        </Routes>
    )
}
